import { ResultType } from '@/enums/ResultType'
import { JaarPeriode, OefenenPeriode, ToetsenPeriode } from '@/enums/Timespan'
import { Yeargroup } from '@/enums/Yeargroup'
import { IResultUser } from '@/interfaces/IResult'
import { methodeToetsResultService } from '@/services/resultaten/methodeToetsResultService'
import { moelijksteOefeningenResultService } from '@/services/resultaten/moelijksteOefeningenResultService'
import { oefeningenResultService } from '@/services/resultaten/oefeningenResultService'
import { notify } from '@kyvg/vue3-notification'
import { ActionTree, GetterTree, MutationTree } from 'vuex'
import { ICategoryResult } from './../../interfaces/ICategoryResult'
import { ICategoryResultFilters } from './../../interfaces/IFilters'

const showVueNotifyError = (message: string) => {
    notify({
        group: 'requests',
        title: 'Foutmelding',
        type: 'error',
        text: `Er is iets misgegaan. Error: ${message}`,
    })
}

class ResultsDetailsState {
    filters: ICategoryResultFilters = {
        YeargroupFilter: Yeargroup.ALLE,
        TimespanFilter: OefenenPeriode.THIS_WEEK,
        ToetsTimespanFilter: ToetsenPeriode.THIS_WEEK,
        VolgenTimespanFilter: JaarPeriode.THIS_SCHOOLYEAR,
        SociaalTimespanFilter: JaarPeriode.THIS_SCHOOLYEAR,
        CompetentiesTimespanFilter: JaarPeriode.THIS_SCHOOLYEAR,
        CategoryFilter: -1,
    }
    catDetailResults: ICategoryResult = {
        gemiddeldeOefenResultaatPerLeerling: [],
        gemiddeldeToetsResultaatPerLeerling: [],
        moeilijksteOefeningen: [],
    }
    loading = {
        oefenenLoading: false,
        moelijksteOefeningenLoading: false,
        toetsenLoading: false,
        volgLoading: false,
        sociaalLoading: false,
        competentiesLoading: false,
    }
}

const getters: GetterTree<ResultsDetailsState, any> = {
    getCatDetailResults(store): () => ICategoryResult {
        const results = store.catDetailResults
        results.gemiddeldeOefenResultaatPerLeerling.sort((a, b) => {
            if (`${a.voornaam.toLowerCase()}${a.achternaam.toLowerCase()}` < `${b.voornaam.toLowerCase()}${b.achternaam.toLowerCase()}`) {
                return -1
            }
            if (`${a.voornaam.toLowerCase()}${a.achternaam.toLowerCase()}` > `${b.voornaam.toLowerCase()}${b.achternaam.toLowerCase()}`) {
                return 1
            }
            return 0
        })

        results.gemiddeldeToetsResultaatPerLeerling.sort((a, b) => {
            if (`${a.voornaam.toLowerCase()}${a.achternaam.toLowerCase()}` < `${b.voornaam.toLowerCase()}${b.achternaam.toLowerCase()}`) {
                return -1
            }
            if (`${a.voornaam.toLowerCase()}${a.achternaam.toLowerCase()}` > `${b.voornaam.toLowerCase()}${b.achternaam.toLowerCase()}`) {
                return 1
            }
            return 0
        })
        return () => results
    },
    getCategoryDetailFilters(store): () => ICategoryResultFilters {
        return () => store.filters
    },
    getSelectedCategoryFilter(store): () => number {
        return () => store.filters.CategoryFilter
    },
    getSelectedCategoryPeriodFilter(store): () => OefenenPeriode {
        return () => store.filters.TimespanFilter
    },
    getSelectedVolgCategoryPeriodFilter(store): () => JaarPeriode {
        return () => store.filters.VolgenTimespanFilter
    },
    getResultsDetailsLoading(store) {
        return () => store.loading.volgLoading || store.loading.oefenenLoading || store.loading.moelijksteOefeningenLoading || store.loading.toetsenLoading || store.loading.sociaalLoading || store.loading.competentiesLoading
    },
}

const mutations: MutationTree<ResultsDetailsState> = {
    setOefenResultaten(state, payload: IResultUser[]) {
        state.catDetailResults.gemiddeldeOefenResultaatPerLeerling = payload
    },
    setToetsResultaten(state, payload: IResultUser[]) {
        state.catDetailResults.gemiddeldeToetsResultaatPerLeerling = payload
    },
    setMoelijksteOefeningResultaten(state, payload: any[]) {
        state.catDetailResults.moeilijksteOefeningen = payload
    },
    setFiltersCategoryDetail(state, payload: ICategoryResultFilters) {
        state.filters = state.filters = { ...state.filters, ...payload }
    },
    setResultDetailsLoading(state, payload) {
        state.loading = payload
    },
}

const actions: ActionTree<ResultsDetailsState, any> = {
    async getOefeningenResultsVak({ commit, state }, vakLabelId: number) {
        state.loading.oefenenLoading = true
        try {
            const data = await oefeningenResultService.getVak(vakLabelId, state.filters.TimespanFilter)
            commit('setOefenResultaten', data.data as IResultUser[])
            return { resultType: ResultType.OEFENINGEN, data: data.data }
        } catch (error: any) {
            showVueNotifyError(error.message)
            throw error
        } finally {
            state.loading.oefenenLoading = false
        }
    },
    async getToetsResultsVak({ commit, state }, vakLabelId: number) {
        state.loading.toetsenLoading = true
        try {
            const data = await methodeToetsResultService.getVak(vakLabelId, state.filters.ToetsTimespanFilter)
            commit('setToetsResultaten', data.data)
            return { resultType: ResultType.TOETSEN, data: data.data }
        } catch (error: any) {
            showVueNotifyError(error.message)
            throw error
        } finally {
            state.loading.toetsenLoading = false
        }
    },
    async getMoelijksteOefeningenResultsVak({ commit, state }, vakLabelId: number) {
        state.loading.moelijksteOefeningenLoading = true
        try {
            const data = await moelijksteOefeningenResultService.getVak(vakLabelId, state.filters.TimespanFilter)
            commit('setMoelijksteOefeningResultaten', data.data)
            return { resultType: ResultType.OEFENINGEN, data: data.data }
        } catch (error: any) {
            showVueNotifyError(error.message)
            throw error
        } finally {
            state.loading.moelijksteOefeningenLoading = false
        }
    },
}

export default {
    state: new ResultsDetailsState(),
    getters: getters,
    mutations: mutations,
    actions: actions,
}
